/**
 * The mineral set, decoded.
 *
 * Eight ore types, each with a NODE — the cluster that sits on a boulder and
 * is mined — and a SHARD, the fragment it breaks into and the thing the player
 * actually picks up. `scripts/bake-minerals.mjs` explains the wire format and
 * why the boulder modelled under each cluster is not in the bundle.
 *
 * Every vertex is tagged `MAT.oreMetal` or `MAT.crystal` by the class the
 * baker DECLARED, not by the mineral's `metallic`. Two reasons for a tag
 * rather than a number: ore of either kind has to refuse things the rest of
 * `fsMesh` does unconditionally — the reference-art detail layer above all,
 * which is a photograph of coral and has no business tiled across a quartz
 * facet — and a gem has to refuse things a native metal takes, and the other
 * way about. A continuous `metallic` cannot express either refusal.
 *
 * uv.x carries HOW FAR UP THE CRYSTAL each vertex is, 0 at the base and 1 at
 * the tip. `vsMesh` overwrites uv.y with the material tag and leaves uv.x
 * alone, so it is the one per-vertex float a mesh may still choose, and this
 * is the most useful thing a faceted crystal can put in it: it runs the
 * internal glow up toward the points and lets the buried base go dark, so a
 * cluster sunk into a rock never shows the cut.
 */

import { MAT, VERT_FLOATS, type MeshData } from './mesh';
import {
  MIN_HI, MIN_LO, MINERAL_DATA, MINERAL_MESHES, MINERAL_TYPES, MIN_VERTS,
  type MineralType,
} from './mineralMesh';

export { MINERAL_TYPES, type MineralType } from './mineralMesh';

/** Ore keys, in the order the manifest lists them. Indices are save-stable. */
export const MINERAL_KEYS = MINERAL_TYPES.map((t) => t.key);

export function mineralType(key: string): MineralType {
  const t = MINERAL_TYPES.find((m) => m.key === key);
  if (!t) throw new Error(`unknown mineral ${key}`);
  return t;
}

/** Renderer slot for a mineral mesh. `min_node_abyssal`, `min_shard_iron`, … */
export function mineralSlot(name: string): string {
  return `min_${name}`;
}

let cached: Map<string, MeshData> | null = null;

/** Every baked mineral mesh by name — `node_iron`, `shard_abyssal`, … */
export function mineralMeshes(): Map<string, MeshData> {
  if (cached) return cached;

  const raw = atob(MINERAL_DATA);
  const bin = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) bin[i] = raw.charCodeAt(i);

  const nv = MIN_VERTS;
  const pos = new Int16Array(bin.buffer, 0, nv * 3);
  const nrm = new Int8Array(bin.buffer, nv * 6, nv * 3);
  const axial = new Uint8Array(bin.buffer, nv * 9, nv);

  const sx = (MIN_HI[0] - MIN_LO[0]) / 65534;
  const sy = (MIN_HI[1] - MIN_LO[1]) / 65534;
  const sz = (MIN_HI[2] - MIN_LO[2]) / 65534;

  // WHICH TAG EACH VERTEX GETS, from the class the baker declared — not from
  // the mineral's `metallic`. See MAT.oreMetal for why that distinction earns
  // a second slot.
  const tagAt = new Float32Array(nv);
  for (const e of MINERAL_MESHES) {
    const tag = e.kind === 'metal' ? MAT.oreMetal : MAT.crystal;
    tagAt.fill(tag, e.vo, e.vo + e.vc);
  }

  const vertices = new Float32Array(nv * VERT_FLOATS);
  for (let k = 0; k < nv; k++) {
    const o = k * VERT_FLOATS;
    vertices[o] = MIN_LO[0] + (pos[k * 3] + 32767) * sx;
    vertices[o + 1] = MIN_LO[1] + (pos[k * 3 + 1] + 32767) * sy;
    vertices[o + 2] = MIN_LO[2] + (pos[k * 3 + 2] + 32767) * sz;
    // Renormalised, not merely scaled — the same reason `subModel2` gives. A
    // facet's specular is the whole look here, so an off-unit normal shows.
    const nx = nrm[k * 3] / 127, ny = nrm[k * 3 + 1] / 127, nz = nrm[k * 3 + 2] / 127;
    const l = Math.hypot(nx, ny, nz) || 1;
    vertices[o + 3] = nx / l;
    vertices[o + 4] = ny / l;
    vertices[o + 5] = nz / l;
    vertices[o + 6] = axial[k] / 255;
    vertices[o + 7] = 0;
    vertices[o + 8] = tagAt[k]!;
  }

  // One varint stream across every mesh in table order, indices local to each.
  let p = nv * 10;
  let prev = 0;
  const readIndices = (count: number) => {
    const out = new Uint32Array(count);
    for (let i = 0; i < count; i++) {
      let shift = 0, z = 0, b = 0;
      do {
        b = bin[p++];
        z |= (b & 127) << shift;
        shift += 7;
      } while (b & 128);
      prev += (z >>> 1) ^ -(z & 1);
      out[i] = prev;
    }
    return out;
  };

  cached = new Map();
  for (const m of MINERAL_MESHES) {
    cached.set(m.name, {
      vertices: vertices.subarray(
        m.vo * VERT_FLOATS, (m.vo + m.vc) * VERT_FLOATS,
      ) as Float32Array<ArrayBuffer>,
      indices: readIndices(m.tc * 3) as Uint32Array<ArrayBuffer>,
    });
  }
  return cached;
}
